import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { IoCloudUploadOutline } from "react-icons/io5";

const FileUploader = ({ onFilesUploaded }) => {
  const [dataURLs, setDataURLs] = useState([]); // Array to hold image previews
  const [uploadedFiles, setUploadedFiles] = useState([]); // Array to track uploaded files for uniqueness

  // Valid MIME types and extensions
  const validMimeTypes = [
    "image/jpg",
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
  ];
  const validExtensions = [".jpg", ".jpeg", ".png", ".gif", "pdf"];

  const onDrop = useCallback(
    (acceptedFiles) => {
      const uniqueFiles = acceptedFiles.filter((file) => {
        const isValidMimeType = validMimeTypes.includes(file.type);
        const isValidExtension = validExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext)
        );

        if (!isValidMimeType || !isValidExtension) {
          console.warn(
            `Skipped "${file.name}" due to invalid MIME type or extension.`
          );
          return false;
        }

        return !uploadedFiles.some(
          (uploadedFile) =>
            uploadedFile.name === file.name && uploadedFile.size === file.size
        );
      });

      if (uniqueFiles.length === 0) {
        alert("No valid files were added.");
        return;
      }

      const newFiles = uniqueFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      setDataURLs((prev) => [...prev, ...newFiles]);
      setUploadedFiles((prev) => [...prev, ...uniqueFiles]);
      onFilesUploaded(uniqueFiles);
    },
    [onFilesUploaded, uploadedFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  // Remove a single image from the preview list
  const removeImage = (index, event) => {
    event.stopPropagation(); // Prevent click from propagating to the dropzone

    setDataURLs((prevDataURLs) => {
      const updatedDataURLs = prevDataURLs.filter((_, i) => i !== index);

      return updatedDataURLs;
    });

    setUploadedFiles((prevUploadedFiles) => {
      const updatedUploadedFiles = prevUploadedFiles.filter(
        (_, i) => i !== index
      );
      onFilesUploaded(updatedUploadedFiles);
      return updatedUploadedFiles;
    });
  };

  return (
    <div
      {...getRootProps()}
      className="border-2 border-gray-300 w-full h-80 flex items-center justify-center rounded-lg"
    >
      <input {...getInputProps()} />
      <div className="text-center">
        {isDragActive ? (
          <p className="text-blue-500">Drop files here...</p>
        ) : (
          <div className="flex flex-col items-center justify-center">
            <IoCloudUploadOutline className="w-[60px] h-auto text-gray-500" />
          </div>
        )}
      </div>

      {/* Display Previews */}
      <div className="grid grid-cols-2 gap-2 mt-4 overflow-y-auto max-h-80">
        {dataURLs.map((item, index) => (
          <div key={index} className="relative group w-60 h-60">
            <img
              src={item.preview}
              alt="preview"
              className="object-cover w-full h-full rounded-lg"
            />
            <button
              onClick={(e) => removeImage(index, e)}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-75 hover:opacity-100 transition-opacity duration-200"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FileUploader;
